import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Mic,
  Video,
  Flame,
  Radar,
  Smile,
  PictureInPicture2,
  MessageSquarePlus,
} from "lucide-react";
import { profileOf } from "@/plugins/profiles";
import type { RoomMode } from "@/engine/state/roomControl";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMediaDevices } from "@/hooks/useMediaDevices";
import { useCameraPreview } from "@/hooks/useCameraPreview";
import { AvatarGrids } from "@/components/room/AvatarGrids";
import { DevicePermissionRow } from "@/components/room/DevicePermissionRow";
import { useSettings } from "@/hooks/useSettings";
import { setSetting } from "@/engine/state/settings";
import { ContactUsForm } from "@/components/ContactUsForm";
import {
  SettingsGroup,
  SettingsSwitchRow,
} from "@/components/settings/SettingsGroup";

/**
 * A settings screen is one section: whoever opens it chooses which, so the
 * panel never carries a tab strip of its own.
 */
export type SettingsSection = "profile" | "application" | "devices" | "contact";

interface DeviceSettingsPanelProps {
  selectedAudioInput?: string;
  selectedVideoInput?: string;
  onAudioInputChange?: (deviceId: string) => void;
  onVideoInputChange?: (deviceId: string) => void;
  /** Room the contact form attaches its message to. */
  roomId?: string;
  /** Which section this panel shows. */
  section?: SettingsSection;
  /** What kind of room this is, when it is being read from inside one. */
  roomMode?: RoomMode;
}

/** What the "Default" option is called, named after the device behind it. */
function defaultLabel(
  t: TFunction,
  list: { deviceId: string; label: string }[],
  defaultId?: string,
) {
  const chosen = list.find((d) => d.deviceId === defaultId) ?? list[0];
  return chosen
    ? t("settings.deviceDefaultNamed", { label: chosen.label })
    : t("settings.deviceDefault");
}

/**
 * Everything that is a preference rather than a device.
 *
 * Two of them decide nothing in a plain room — it mixes flat for every
 * listener whatever the spatial preference says, and it has no campfire to be
 * audible — so they are not drawn there. Hidden and not disabled: they are not
 * about to become useful, the way a view button waiting for a weak device is.
 */
function ApplicationSection(props: {
  spatialAudio: boolean;
  campfireSound: boolean;
  autoPipOnShare: boolean;
  /** Absent outside a room, where the visitor is choosing rather than reading. */
  roomMode?: RoomMode;
}) {
  const { spatialAudio, campfireSound, autoPipOnShare, roomMode } = props;
  const { t } = useTranslation();
  // A room that places no voice has nothing to have the first preference
  // about: it mixes flat whatever the listener chose. A room with no campfire
  // has no fire to hear, so the second is the scene's alone — a bubbles
  // room places voices on its map and has no fire (#913).
  const placed = profileOf(roomMode).acoustics === "spatial";
  const inScene = profileOf(roomMode).hasScene;

  return (
    <div className="flex flex-col divide-y divide-border">
      {placed && (
        <SettingsSwitchRow
          data-testid="preference-spatial-audio"
          id="spatial-audio"
          icon={<Radar />}
          label={t("settings.spatialAudio")}
          checked={spatialAudio}
          onCheckedChange={(v) => setSetting("spatialAudio", v)}
        />
      )}
      {inScene && (
        <SettingsSwitchRow
          data-testid="preference-campfire-sound"
          id="campfire-sound"
          icon={<Flame />}
          label={t("settings.campfireSound")}
          checked={campfireSound}
          onCheckedChange={(v) => setSetting("campfireSound", v)}
        />
      )}
      <SettingsSwitchRow
        data-testid="preference-auto-pip"
        id="auto-pip"
        icon={<PictureInPicture2 />}
        label={t("settings.autoPip")}
        checked={autoPipOnShare}
        onCheckedChange={(v) => setSetting("autoPipOnShare", v)}
      />
    </div>
  );
}

export function DeviceSettingsPanel({
  selectedAudioInput,
  selectedVideoInput,
  onAudioInputChange,
  onVideoInputChange,
  roomId,
  section = "profile",
  roomMode,
}: DeviceSettingsPanelProps) {
  const { t } = useTranslation();
  const {
    audioInputs,
    videoInputs,
    defaultAudioInput,
    defaultVideoInput,
    refresh,
  } = useMediaDevices();

  // The camera only spins up on the section that shows it.
  const { videoRef, isActive: isCameraActive } = useCameraPreview(
    selectedVideoInput,
    section === "devices",
  );

  const { campfireSound, spatialAudio, autoPipOnShare } = useSettings();

  if (section === "profile") {
    return (
      <div className="space-y-4">
        <section className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Smile className="w-4 h-4" />
            {t("settings.avatar")}
          </Label>
          <AvatarGrids />
        </section>
      </div>
    );
  }

  if (section === "application") {
    return (
      <ApplicationSection
        spatialAudio={spatialAudio}
        campfireSound={campfireSound}
        autoPipOnShare={autoPipOnShare}
        roomMode={roomMode}
      />
    );
  }

  if (section === "devices") {
    return (
      <div className="space-y-4">
        <SettingsGroup
          title={
            <>
              <Mic className="size-3.5" />
              {t("settings.microphone")}
            </>
          }
        >
          <div className="flex min-w-0 flex-col gap-2 p-3">
            <Select
              key={`audio-in-${audioInputs.length}`}
              value={selectedAudioInput ?? ""}
              onValueChange={onAudioInputChange}
            >
              <SelectTrigger className="w-full h-8 text-xs">
                <SelectValue
                  placeholder={defaultLabel(t, audioInputs, defaultAudioInput)}
                />
              </SelectTrigger>
              <SelectContent>
                {audioInputs.map((d) => (
                  <SelectItem key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DevicePermissionRow kind="microphone" onGranted={refresh} />
          </div>
        </SettingsGroup>

        <SettingsGroup
          title={
            <>
              <Video className="size-3.5" />
              {t("settings.camera")}
            </>
          }
        >
          <div className="flex min-w-0 flex-col gap-2 p-3">
            <div className="relative rounded-lg overflow-hidden bg-muted aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {!isCameraActive && (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-xs">
                  {t("settings.noVideo")}
                </div>
              )}
            </div>
            <Select
              key={`video-${videoInputs.length}`}
              value={selectedVideoInput ?? ""}
              onValueChange={onVideoInputChange}
            >
              <SelectTrigger className="w-full h-8 text-xs">
                <SelectValue
                  placeholder={defaultLabel(t, videoInputs, defaultVideoInput)}
                />
              </SelectTrigger>
              <SelectContent>
                {videoInputs.map((d) => (
                  <SelectItem key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DevicePermissionRow kind="camera" onGranted={refresh} />
          </div>
        </SettingsGroup>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <MessageSquarePlus className="w-4 h-4" />
          {t("settings.contactUs")}
        </Label>
        <ContactUsForm roomId={roomId} />
      </section>
    </div>
  );
}
